import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
	Home,
	Search,
	Menu,
	User,
	ChevronLeft,
	ChevronRight,
	Edit2,
	MessageSquare,
	X,
	Download,
	BarChart3,
} from "lucide-react";
import { CraftButton } from "@craft-design-system/core-components-react";
import Header from "../components/Headers/Header";
import PTSachNrDataTable from "../components/Datatables/PTSachNrDataTable";
import PTSachNrSidebar from "../components/Sidebars/PTSachNrSidebar";
import SupplyTypeAllocationJoyrideTour from "../components/JoyrideTours/SupplyTypeAllocationJoyrideTour";
import PTSachNrExportModal from "../components/Modals/PTSachNrExportModal";
import { BorderStyle } from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	Typography,
	Dialog,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import _ from "lodash";
import { getApiBaseUrl } from "../utils/apiConfig";
import { apiClient } from "../services/api";
import {
	shouldShowTour,
	markTourCompleted,
	PAGE_NAMES,
} from "../utils/firstTimeVisit";
import { signOut } from "../utils/authorizationUtils";
const ENABLE_BULK_EDIT_FOR_BOTH_VIEW = false;

const PartsTrackingTool = ({ user }) => {
	useEffect(() => {
		document.title = "MAN PTT - Supply Type";
	}, []);
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [selectedProject, setSelectedProject] = useState([]);
	const [selectedProgramm, setSelectedProgramm] = useState([]);
	const [selectedViewType, setSelectedViewType] = useState("Prototype"); // Keep this state here  // Get user display name function
	const getUserDisplayName = () => {
		if (user?.profile?.preferred_username) {
			return user.profile.preferred_username;
		}
		if (user?.profile?.email) {
			return user.profile.email.split("@")[0];
		}
		// Fall back to the username stored in localStorage (set from JWT at login)
		const storedUsername = localStorage.getItem("username");
		if (storedUsername) {
			return storedUsername;
		}
		return "User";
	};
	// Get full user email for authorization (mirrors VFFPage logic)
	const getUserEmail = () => {
		// 1. Best case: full email is directly available
		if (user?.profile?.email) {
			return user.profile.email;
		}
		// 2. preferred_username is already a full email
		if (
			user?.profile?.preferred_username &&
			user.profile.preferred_username.includes("@")
		) {
			return user.profile.preferred_username;
		}
		// 3. Check localStorage for a stored email
		const localStorageUsername = localStorage.getItem("username");
		if (localStorageUsername && localStorageUsername.includes("@")) {
			return localStorageUsername;
		}
		// 4. preferred_username is just the username part — append @man.eu domain
		if (user?.profile?.preferred_username) {
			const username = user.profile.preferred_username;
			// Append the corporate domain so the backend DB lookup works
			return `${username}@man.eu`;
		}
		return null;
	};

	// Function to navigate to dashboard with main view
	const navigateToDashboard = () => {
		window.location.href = "/dashboard?view=main";
	};
	const [selectedPurchaseType, setSelectedPurchaseType] = useState([]);
	const [selectedPurchaseTypeSystem, setSelectedPurchaseTypeSystem] = useState(
		[],
	);
	const [selectedSeTeams, setSelectedSeTeams] = useState([]);
	const [selectedGxClassification, setSelectedGxClassification] = useState([]);
	const [selectedRampUpGroup, setSelectedRampUpGroup] = useState([]);
	const [selectedBemiErf, setSelectedBemiErf] = useState([]);
	const [selectedFlag, setSelectedFlag] = useState([]);
	const [selectedAenderungsnummer, setSelectedAenderungsnummer] = useState([]);
	const [selectedChangeDescription, setSelectedChangeDescription] = useState(
		[],
	);
	const [selectedFet, setSelectedFet] = useState({ from: "", to: "" });
	const [selectedDept, setSelectedDept] = useState([]);
	const [selectedPartNoPrefix, setSelectedPartNoPrefix] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [searchError, setSearchError] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(100);
	const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);
	const [allSelected, setAllSelected] = useState(false);
	const [showFilterTooltip, setShowFilterTooltip] = useState(false);
	const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(true);
	const [filterOptionsError, setFilterOptionsError] = useState(null);
	const [isFilterClearing, setIsFilterClearing] = useState(false);
	const [isPTSEmailExportModalOpen, setIsPTSEmailExportModalOpen] =
		useState(false);
	const [showMismatchOnly, setShowMismatchOnly] = useState(false);
	const [showBulkAccessDeniedModal, setShowBulkAccessDeniedModal] =
		useState(false);

	// Tour state
	const [isTourOpen, setIsTourOpen] = useState(false);
	// Dashboard filter states - similar to VFFPage
	const [dashboardFiltersApplied, setDashboardFiltersApplied] = useState(false);
	const [isDashboardFilterLoading, setIsDashboardFilterLoading] =
		useState(false);
	const [isFromDashboard, setIsFromDashboard] = useState(false);
	const [dashboardDataLoaded, setDashboardDataLoaded] = useState(false);
	// Function to parse URL parameters and apply dashboard filters
	const applyDashboardFilters = () => {
		const urlParams = new URLSearchParams(window.location.search);
		const seTeamFilter = urlParams.get("se_team_filter");
		const purchaseTypeFilter = urlParams.get("purchase_type_filter");
		const projectFilter = urlParams.get("project_filter");
		const programmFilter = urlParams.get("programm_filter");
		const viewTypeFilter = urlParams.get("view_type_filter"); // 'Prototype' | 'VFF' | 'Series'
		const fromDashboard = urlParams.get("from_dashboard");

		// Set if we're coming from dashboard
		if (fromDashboard === "true") {
			setIsFromDashboard(true);
		}

		if (
			fromDashboard === "true" &&
			(seTeamFilter || purchaseTypeFilter) &&
			!dashboardFiltersApplied
		) {
			console.log("Applying dashboard filters:", {
				seTeamFilter,
				purchaseTypeFilter,
				projectFilter,
				programmFilter,
				viewTypeFilter,
			});

			// Show loading overlay while applying filters
			setIsDashboardFilterLoading(true);

			// Resolve which view type to activate (default to Prototype)
			const resolvedViewType = ["VFF", "Series", "Prototype"].includes(
				viewTypeFilter,
			)
				? viewTypeFilter
				: "Prototype";
			setSelectedViewType(resolvedViewType);

			// Set the items-per-page default that matches the view type
			const defaultItemsPerPage =
				resolvedViewType === "VFF" || resolvedViewType === "Series" ? 125 : 100;
			setItemsPerPage(defaultItemsPerPage);

			// Set the filters based on what's provided
			if (seTeamFilter) {
				setSelectedSeTeams([seTeamFilter]);
			}
			if (purchaseTypeFilter) {
				setSelectedPurchaseType([purchaseTypeFilter]);
			}
			if (projectFilter) {
				setSelectedProject([projectFilter]);
			}
			if (programmFilter) {
				setSelectedProgramm([programmFilter]);
			}

			// Mark that we've applied these filters to prevent reapplication
			setDashboardFiltersApplied(true);

			// Apply the filters through the data table
			setTimeout(() => {
				if (dataTableRef.current && dataTableRef.current.fetchData) {
					const filters = {
						project: projectFilter ? [projectFilter] : [],
						programm: programmFilter ? [programmFilter] : [],
						purchaseType: purchaseTypeFilter ? [purchaseTypeFilter] : [],
						purchaseTypeSystem: [],
						seTeams: seTeamFilter ? [seTeamFilter.toLowerCase()] : [],
						gxClassification: [],
						rampUpGroup: [],
						bemiErf: [],
						aenderungsnummer: [],
						changeDescription: [],
						dept: [],
						flag: [],
						globalSearch: "",
					};
					console.log(
						"Applying dashboard filters to PTSachNr table:",
						filters,
						"viewType:",
						resolvedViewType,
					);

					// Fetch data and hide loading overlay when complete
					const fetchPromise = dataTableRef.current.fetchData(
						filters,
						1,
						defaultItemsPerPage,
						resolvedViewType,
					);

					// Handle both promise and non-promise returns
					if (fetchPromise && typeof fetchPromise.then === "function") {
						fetchPromise
							.then(() => {
								setIsDashboardFilterLoading(false);
							})
							.catch((error) => {
								console.error("Error applying dashboard filters:", error);
								setIsDashboardFilterLoading(false);
							});
					} else {
						// Fallback if fetchData doesn't return a promise
						setTimeout(() => {
							setIsDashboardFilterLoading(false);
						}, 2000);
					}
				} else {
					// Fallback to hide loading if data table ref is not available
					setIsDashboardFilterLoading(false);
				}
			}, 1000); // Delay to ensure component is fully loaded

			// Clear URL parameters to clean up the URL
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	};

	// Prevent duplicate API calls in React.StrictMode (development only)
	const hasInitialized = useRef(false);
	const [filterOptions, setFilterOptions] = useState({
		programms: [],
		projects: [],
		purchaseTypes: [],
		purchaseTypeSystems: [],
		seTeams: [],
		rampUpGroups: [],
		flags: [],
		aenderungsnummers: [],
		changeDescriptions: [],
		fets: [],
		depts: [],
		projectPurchaseTypeMapping: {},
		projectSeTeamsMapping: {},
		projectRampUpGroupMapping: {},
		projectAenderungsnummerMapping: {},
		projectChangeDescriptionMapping: {},
		projectDeptMapping: {},
		programmPurchaseTypeMapping: {},
		programmSeTeamsMapping: {},
		programmProjectMapping: {},
		programmRampUpGroupMapping: {},
		programmAenderungsnummerMapping: {},
		programmChangeDescriptionMapping: {},
		programmDeptMapping: {},
	});

	// State for edit options - these are all possible values for editing
	const [editOptions, setEditOptions] = useState({
		projects: [],
		purchaseTypes: [],
		purchaseTypeSystems: [],
		total_req: [],
		seTeams: [],
		gxClassifications: [],
		rampUpGroups: [],
		changeDescriptions: [],
		fets: [],
		aenderungsnummers: [],
		depts: [],
		bemiErf: [],
		suppliers: [],
	});
	const dataTableRef = useRef();

	// State for column visibility
	const [columnVisibility, setColumnVisibility] = useState({
		rampUpGroup: true,
		seTeams: true,
		purchaseType: true,
		gxClassification: true,
		supplier: true,
	});

	const handleColumnVisibilityChange = (newVisibility) => {
		setColumnVisibility(newVisibility);
	};

	// Handle selection of items in the data table
	const handleSelectItems = (items) => {
		setSelectedItems(items);
		setAllSelected(
			items.length > 0 &&
				items.length === (dataTableRef.current?.getAllData()?.length || 0),
		);
	};
	// Optimized data loading sequence for better performance
	useEffect(() => {
		// Prevent duplicate calls in React.StrictMode (development only)
		if (hasInitialized.current) {
			return;
		}
		hasInitialized.current = true;
		// Check if tour should continue from landing page
		const shouldContinueTour = localStorage.getItem("continueSupplyTypeTour");
		if (shouldContinueTour === "true") {
			// (Kept for backward-compatibility; the landing page no longer sets this
			// flag.) Start the tour after a brief delay.
			localStorage.removeItem("continueSupplyTypeTour");
			setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SUPPLY_TYPE);
			}, 1000);
		} else if (shouldShowTour(PAGE_NAMES.SUPPLY_TYPE)) {
			// First time visiting Supply Type page directly
			setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SUPPLY_TYPE);
			}, 1500);
		}
		const fetchDataAndOptions = async () => {
			try {
				setIsFilterOptionsLoading(true);
				setFilterOptionsError(null);

				const defaultFilters = {
					project: [],
					programm: [],
					purchaseType: [],
					seTeams: [],
					gxClassification: [],
					rampUpGroup: [],
					bemiErf: [],
					aenderungsnummers: [],
					changeDescriptions: [],
					fets: [],
					depts: [],
				}; // Load filter options from dedicated PTSachNr endpoint (fetches directly from Athena)
				let filterData;
				try {
					console.log("Loading PTSachNr filter options from Athena...");
					filterData = await apiClient
						.get(`/api/ptsachnr-filter-options?view_type=${encodeURIComponent(selectedViewType)}`)
						.then((response) => {
							console.log(
								"Successfully loaded PTSachNr filter options from Athena",
							);
							return response.data;
						})
						.catch((error) => {
							console.error("Error fetching PTSachNr filter options:", error);
							setFilterOptionsError("Failed to load filter options");
							return {};
						});
				} catch (error) {
					console.error("Error fetching PTSachNr filter options:", error);
					setFilterOptionsError("Failed to load filter options");
					filterData = {};
				}
				setFilterOptions({
					programms: filterData.programms || [],
					projects: filterData.projects || [],
					purchaseTypes: Array.from(
						new Set([...(filterData.purchaseTypes || []), "Z", "NRV"]),
					).sort(),
					purchaseTypeSystems: filterData.purchaseTypeSystems || [],
					seTeams: filterData.seTeams || [],
					rampUpGroups: filterData.rampUpGroups || [],
					flags: filterData.flags || [],
					aenderungsnummers: filterData.aenderungsnummers || [],
					changeDescriptions: filterData.changeDescriptions || [],
					fets: filterData.fets || [],
					depts: filterData.depts || [],
					// dynamic-filtering mappings
					projectPurchaseTypeMapping:
						filterData.projectPurchaseTypeMapping || {},
					projectSeTeamsMapping: filterData.projectSeTeamsMapping || {},
					projectRampUpGroupMapping: filterData.projectRampUpGroupMapping || {},
					projectAenderungsnummerMapping:
						filterData.projectAenderungsnummerMapping || {},
					projectChangeDescriptionMapping:
						filterData.projectChangeDescriptionMapping || {},
					projectDeptMapping: filterData.projectDeptMapping || {},
					programmPurchaseTypeMapping:
						filterData.programmPurchaseTypeMapping || {},
					programmSeTeamsMapping: filterData.programmSeTeamsMapping || {},
					programmProjectMapping: filterData.programmProjectMapping || {},
					programmRampUpGroupMapping:
						filterData.programmRampUpGroupMapping || {},
					programmAenderungsnummerMapping:
						filterData.programmAenderungsnummerMapping || {},
					programmChangeDescriptionMapping:
						filterData.programmChangeDescriptionMapping || {},
					programmDeptMapping: filterData.programmDeptMapping || {},
				});
				setEditOptions({
					projects: filterData.projects || [],
					purchaseTypes: Array.from(
						new Set([...(filterData.purchaseTypes || []), "Z", "NRV"]),
					).sort(),
					purchaseTypeSystems: [],
					total_req: [],
					seTeams: filterData.seTeams || [],
					gxClassifications: [],
					rampUpGroups: filterData.rampUpGroups || [],
					bemiErf: [],
					suppliers: filterData.suppliers || [],
				});

				// Remove this call - let PTSachNrDataTable handle its own initialization
				// if (dataTableRef.current && dataTableRef.current.fetchData) {
				//   await dataTableRef.current.fetchData(defaultFilters, 1, itemsPerPage, selectedViewType);
				//   setSelectedProject(defaultFilters.project);
				// }
			} catch (error) {
				console.error("Error fetching data and options:", error);
				setFilterOptionsError("Failed to load filter options");
			} finally {
				setIsFilterOptionsLoading(false);
			}
		};
		fetchDataAndOptions();

		// Check if coming from dashboard and set state immediately
		const urlParams = new URLSearchParams(window.location.search);
		const fromDashboard = urlParams.get("from_dashboard");
		if (fromDashboard === "true") {
			setIsFromDashboard(true);
		}

		applyDashboardFilters(); // Apply dashboard filters on initial load
	}, []);

	// Check for dashboard filters after component mounts
	useEffect(() => {
		// Apply dashboard filters after a short delay to ensure components are loaded
		setTimeout(applyDashboardFilters, 500);
	}, [dashboardFiltersApplied]);

	// Check for first-time visit
	useEffect(() => {
		if (shouldShowTour(PAGE_NAMES.SUPPLY_TYPE)) {
			// Small delay to ensure page is fully loaded
			const timer = setTimeout(() => {
				setIsTourOpen(true);
				markTourCompleted(PAGE_NAMES.SUPPLY_TYPE);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, []);

	const fontStyles = {
		regular: {
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		bold: {
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		},
	};
	const styles = {
		mainContainer: {
			display: "flex",
			flexDirection: "column",
			height: "100vh",
			width: "100vw",
			paddingTop: "calc(74px + var(--banner-height, 0px))",
			boxSizing: "border-box",
			backgroundColor: "#f0f0f0",
			overflow: "hidden",
			...fontStyles.regular,
		},
		contentWrapper: {
			display: "flex",
			flex: 1,
			overflow: "hidden",
			padding: "0 0px 0px 0px", // Removed right padding to allow full width
		},
		mainContent: {
			flex: 1,
			overflow: "hidden",
			backgroundColor: "white",
			marginLeft: "0px",
			display: "flex",
			flexDirection: "column",
		},
		tableContainer: {
			flex: 1,
			overflow: "auto",
			padding: "0px",
			width: "100%",
			display: "flex",
			flexDirection: "column",
			minHeight: 0,
		},
		searchContainer: {
			padding: "10px 0px 5px 0px",
			backgroundColor: "#f1f1f1",
			borderBottom: "1px solid #eee",
			width: "100%",
		},
		modal: {
			position: "fixed",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			// Must sit above the fixed app header/navigation (zIndex 1100). The
			// modal is also portaled to document.body so no ancestor stacking
			// context or overflow container can trap/clip it.
			zIndex: 1300,
		},
		modalContent: {
			backgroundColor: "white",
			width: "80%",
			maxWidth: "1000px",
			maxHeight: "90vh",
			display: "flex",
			flexDirection: "column",
			boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
		},
		modalHeader: {
			padding: "0px 20px",
			borderBottom: "1px solid #eee",
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			backgroundColor: "#f5f5f5",
		},
		modalBody: {
			padding: "15px 10px",
			overflowY: "auto",
			flex: 1,
		},
		modalFooter: {
			padding: "10px 20px",
			borderTop: "1px solid #eee",
			display: "flex",
			justifyContent: "flex-end",
			gap: "10px",
			backgroundColor: "#f5f5f5",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			marginBottom: "20px",
		},
		tableHeader: {
			...fontStyles.bold,
			padding: "12px",
			textAlign: "left",
			fontSize: "12px",
			color: "#666",
		},
		tableCell: {
			...fontStyles.regular,
			padding: "12px",
			textAlign: "left",
		},
		select: {
			...fontStyles.regular,
			width: "100%",
			padding: "8px",
			border: "1px solid #ddd",
			backgroundColor: "white",
		},
		button: {
			...fontStyles.regular,
			backgroundColor: "#666",
			color: "white",
			border: "none",
			padding: "8px 16px",
			cursor: "pointer",
		},
		label: {
			...fontStyles.regular,
			fontSize: "14px",
			color: "#333",
		},
	};

	const sidebarStyles = {
		container: {
			width: isSidebarCollapsed ? "50px" : "250px",
			backgroundColor: "#f0f0f0",
			padding: 0,
			transition: "all 0.3s ease",
			position: "relative",
			overflow: "hidden",
			padding: "0px 0px 10px 0px",
			borderRight: "2px solid black",
			borderTop: "2px solid black",
			height: "100%",
			...fontStyles.regular,
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "15px",
			backgroundColor: "#f0f0f0",
			justifyContent: isSidebarCollapsed ? "center" : "space-between",
		},
		title: {
			...fontStyles.bold,
			fontSize: "16px",
			margin: 0,
			display: "flex",
			alignItems: "center",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
		},
		content: {
			opacity: isSidebarCollapsed ? 0 : 1,
			visibility: isSidebarCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			width: "100%",
			padding: "15px",
		},
		filterLabel: {
			...fontStyles.regular,
			display: "block",
			fontSize: "14px",
			marginBottom: "6px",
			color: "#333",
		},
		select: {
			...fontStyles.regular,
			width: "90%",
			padding: "8px",
			border: "1px solid #ddd",
			marginBottom: "16px",
			backgroundColor: "white",
			cursor: "pointer",
		},
		filterGroup: {
			marginBottom: "15px",
		},
	};

	const handleSidebarToggle = () => {
		setIsSidebarCollapsed(!isSidebarCollapsed);
	};
	// Debounce search to avoid too many requests
	const debouncedSearch = React.useCallback(
		_.debounce((query, filters) => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Merge the search query with all currently active sidebar filters
				// so that project/programm/etc. are preserved when searching
				dataTableRef.current.fetchData(
					{ ...filters, search: query },
					undefined,
					undefined,
					selectedViewType,
				);
			}
		}, 500),
		[selectedViewType], // Include selectedViewType in dependencies
	);

	// Search submit handler for form submission
	const handleSearchSubmit = (e) => {
		e.preventDefault();
		handleSearch(searchInput);
	};

	// Validate search query for special characters
	const validateSearchQuery = (query) => {
		// Allow alphanumeric characters, spaces, hyphens, and dots for part numbers
		// Hyphens and dots are common in part numbers like "6KT-SCHRAUBE M10X35-Z1-10.9-MAN183-B1"
		const regex = /^[a-zA-Z0-9\s\-\.]*$/;
		return regex.test(query);
	};

	// Search handler for direct search
	const handleSearch = async (query) => {
		// Clear previous errors
		setSearchError("");
		console.log(
			"Search initiated with query:",
			query,
			"in view type:",
			selectedViewType,
		);
		// If query is empty, proceed with empty search
		if (!query.trim()) {
			setSearchQuery("");
			setSelectedItems([]);
			setAllSelected(false);
			debouncedSearch("", {
				project: selectedProject,
				programm: selectedProgramm,
				purchaseType: selectedPurchaseType,
				purchaseTypeSystem: selectedPurchaseTypeSystem,
				seTeams: selectedSeTeams,
				rampUpGroup: selectedRampUpGroup,
				flag: selectedFlag,
				aenderungsnummer: selectedAenderungsnummer,
				changeDescription: selectedChangeDescription,
				fet: selectedFet,
				dept: selectedDept,
				partNoPrefix: selectedPartNoPrefix,
			});
			return;
		}

		// Validate query for special characters
		if (!validateSearchQuery(query)) {
			setSearchError(
				"Please use only letters, numbers, and spaces in your search",
			);
			return;
		}

		// Additional validation for query length
		if (query.length > 100) {
			setSearchError(
				"Search query is too long. Maximum 100 characters allowed.",
			);
			return;
		}

		setSearchQuery(query);
		// Clear selected items when search changes
		setSelectedItems([]);
		setAllSelected(false);
		try {
			// Trigger debounced search — pass current sidebar filters so they are preserved
			await debouncedSearch(query, {
				project: selectedProject,
				programm: selectedProgramm,
				purchaseType: selectedPurchaseType,
				purchaseTypeSystem: selectedPurchaseTypeSystem,
				seTeams: selectedSeTeams,
				rampUpGroup: selectedRampUpGroup,
				flag: selectedFlag,
				aenderungsnummer: selectedAenderungsnummer,
				changeDescription: selectedChangeDescription,
				fet: selectedFet,
				dept: selectedDept,
				partNoPrefix: selectedPartNoPrefix,
			});
		} catch (error) {
			console.error("Search error:", error);
			setSearchError(
				"An error occurred while searching. Please try again with different terms.",
			);
		}
	};
	const handleSearchClear = () => {
		setSearchInput("");
		setSearchQuery("");
		setSearchError("");
		if (dataTableRef.current && dataTableRef.current.fetchData) {
			// Preserve all active sidebar filters when clearing the search
			dataTableRef.current.fetchData(
				{
					search: "",
					project: selectedProject,
					programm: selectedProgramm,
					purchaseType: selectedPurchaseType,
					purchaseTypeSystem: selectedPurchaseTypeSystem,
					seTeams: selectedSeTeams,
					rampUpGroup: selectedRampUpGroup,
					flag: selectedFlag,
					aenderungsnummer: selectedAenderungsnummer,
					changeDescription: selectedChangeDescription,
					fet: selectedFet,
					dept: selectedDept,
					partNoPrefix: selectedPartNoPrefix,
				},
				undefined,
				undefined,
				selectedViewType,
			);
		}
	};

	const handleBulkEditOpen = () => {
		setIsBulkEditOpen(true);
	};

	const handleBulkEditClose = () => {
		setIsBulkEditOpen(false);
	};
	const handleBulkEdit = () => {
		if (!selectedItems || selectedItems.length === 0) {
			alert("Please select at least 1 item to bulk edit.");
			return;
		}
		if (selectedItems.length > 100) {
			alert(
				`Bulk edit is limited to 100 records at a time. You have selected ${selectedItems.length} records. Please deselect some items and try again.`,
			);
			return;
		}
		setIsBulkEditOpen(true);
	};
	const handleCloseBulkEdit = () => {
		setIsBulkEditOpen(false);
	};

	const handleSaveBulkEdit = () => {
		// Implement save functionality here
		setIsBulkEditOpen(false);
	};

	// Tour handlers
	const handleStartTour = () => {
		setIsTourOpen(true);
	};
	const handleCloseTour = () => {
		setIsTourOpen(false);
		// Ensure the page is marked as visited when tour is closed
		markTourCompleted(PAGE_NAMES.SUPPLY_TYPE);
	};

	const handleOpenPTSEmailExportModal = () =>
		setIsPTSEmailExportModalOpen(true);
	const handleClosePTSEmailExportModal = () =>
		setIsPTSEmailExportModalOpen(false);

	const handleSelectItem = (item) => {
		const isSelected = selectedItems.some(
			(selected) => selected.id === item.id,
		);
		if (isSelected) {
			setSelectedItems(
				selectedItems.filter((selected) => selected.id !== item.id),
			);
		} else {
			setSelectedItems([...selectedItems, item]);
		}
	};

	const handleSelectAll = () => {
		if (allSelected) {
			setSelectedItems([]);
		}
		setAllSelected(!allSelected);
	};
	const handleFiltersChange = (filters, viewType) => {
		// Update local state for filters
		setSelectedProject(filters.project || []);
		setSelectedProgramm(filters.programm || []);
		setSelectedPurchaseType(filters.purchaseType || []);
		setSelectedPurchaseTypeSystem(filters.purchaseTypeSystem || []);
		setSelectedSeTeams(filters.seTeams || []);
		setSelectedGxClassification(filters.gxClassification || []);
		setSelectedRampUpGroup(filters.rampUpGroup || []);
		setSelectedBemiErf(filters.bemiErf || []);
		setSelectedFlag(filters.flag || []);
		setSelectedAenderungsnummer(filters.aenderungsnummer || []);
		setSelectedChangeDescription(filters.changeDescription || []);
		setSelectedFet(filters.fet || { from: "", to: "" });
		setSelectedDept(filters.dept || []);
		setSelectedPartNoPrefix(filters.partNoPrefix || []);
		setShowMismatchOnly(!!filters.showMismatchOnly);
		setSelectedViewType(viewType);
		// Check if any filters are applied
		const hasFilters =
			(filters.project && filters.project.length > 0) ||
			(filters.programm && filters.programm.length > 0) ||
			(filters.purchaseType && filters.purchaseType.length > 0) ||
			(filters.purchaseTypeSystem && filters.purchaseTypeSystem.length > 0) ||
			(filters.seTeams && filters.seTeams.length > 0) ||
			(filters.gxClassification && filters.gxClassification.length > 0) ||
			(filters.rampUpGroup && filters.rampUpGroup.length > 0) ||
			(filters.bemiErf && filters.bemiErf.length > 0) ||
			(filters.flag && filters.flag.length > 0) ||
			(filters.aenderungsnummer && filters.aenderungsnummer.length > 0) ||
			(filters.changeDescription && filters.changeDescription.length > 0) ||
			(filters.fet && (filters.fet.from || filters.fet.to)) ||
			(filters.dept && filters.dept.length > 0) ||
			(filters.partNoPrefix && filters.partNoPrefix.length > 0);

		// When filters are applied, use 100 items per page, otherwise use current items per page
		const itemsToUse = hasFilters ? 100 : itemsPerPage;

		// Update items per page if filters are applied
		if (hasFilters && itemsPerPage !== 100) {
			setItemsPerPage(100);
		}

		// Call the fetchData method of the DataTable component
		// When applying new filters, always start from page 1
		if (dataTableRef.current && dataTableRef.current.fetchData) {
			dataTableRef.current.fetchData(
				{
					...filters,
					programm: filters.programm || [],
					flag: filters.flag || [],
					aenderungsnummer: filters.aenderungsnummer || [],
					changeDescription: filters.changeDescription || [],
					fet: filters.fet || { from: "", to: "" },
					dept: filters.dept || [],
					search: searchInput || "",
				},
				1,
				itemsToUse,
				viewType,
			);
		}
	};
	const handleClearFilters = () => {
		// Set clearing state to immediately update active filters display
		setIsFilterClearing(true);

		// Reset all filter states
		setSelectedProject([]);
		setSelectedProgramm([]);
		setSelectedPurchaseType([]);
		setSelectedPurchaseTypeSystem([]);
		setSelectedSeTeams([]);
		setSelectedGxClassification([]);
		setSelectedRampUpGroup([]);
		setSelectedBemiErf([]);
		setSelectedFlag([]); // Clear flag filter    setSelectedAenderungsnummer([]); // Clear Change No filter
		setSelectedChangeDescription([]); // Clear Change Description filter
		setSelectedFet({ from: "", to: "" }); // Clear FET range filter
		setSelectedDept([]); // Clear Dept filter
		setSelectedPartNoPrefix([]); // Clear Part No Prefix filter
		setShowMismatchOnly(false); // Clear mismatch filter
		setSearchInput("");
		setSearchQuery("");
		setSearchError("");
		// Reset view type to Prototype to prevent race condition
		setSelectedViewType("Prototype");
		// Reset items per page to 100 (Prototype default) when clearing filters
		setItemsPerPage(100);
		// Use setTimeout to ensure state updates are processed before fetching data
		setTimeout(() => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Reset the initial data loaded flag so it can load initial data again
				if (dataTableRef.current.resetInitialDataFlag) {
					dataTableRef.current.resetInitialDataFlag();
				}
				// Force refresh to bypass cache and ensure API request is generated
				dataTableRef.current.fetchData({}, 1, 100, "Prototype", true);
			}
			// Reset clearing state after data fetch
			setIsFilterClearing(false);
		}, 0);
	};
	const handleViewTypeChange = (newViewType) => {
		// If view type is actually changing
		if (newViewType !== selectedViewType) {
			// Selections must never carry across View Types. Clear the selected
			// rows and the "select all" flag so the Bulk Edit count resets to 0
			// immediately on switch. The data table's checkboxes derive their
			// checked state from the selectedItems prop, so emptying it here also
			// clears them visually. (Only runs when the View Type actually
			// changes, so same-view-type behavior is untouched.)
			setSelectedItems([]);
			setAllSelected(false);

			// Set default items per page based on view type
			let defaultItemsPerPage;
			if (newViewType === "VFF" || newViewType === "Series") {
				defaultItemsPerPage = 125;
			} else if (newViewType === "Both") {
				defaultItemsPerPage = 50;
			} else {
				defaultItemsPerPage = 100; // Prototype default - show 100 records
			}

			// Update the local state with the new default items per page
			setItemsPerPage(defaultItemsPerPage);
			// Reset to first page when changing view type
			const currentFilters = {
				project: selectedProject,
				programm: selectedProgramm,
				purchaseType: selectedPurchaseType,
				purchaseTypeSystem: selectedPurchaseTypeSystem,
				seTeams: selectedSeTeams,
				gxClassification: selectedGxClassification,
				rampUpGroup: selectedRampUpGroup,
				bemiErf: selectedBemiErf,
				aenderungsnummer: selectedAenderungsnummer,
				changeDescription: selectedChangeDescription,
				fet: selectedFet,
				dept: selectedDept,
				partNoPrefix: selectedPartNoPrefix,
				flag: selectedFlag,
				search: searchQuery,
			};
			// Update the view type
			setSelectedViewType(newViewType); // Reload filter options from Athena for the new view type
			apiClient
				.get(`/api/ptsachnr-filter-options?view_type=${encodeURIComponent(newViewType)}`)
				.then((response) => {
					const filterData = response.data;
					setFilterOptions({
						programms: filterData.programms || [],
						projects: filterData.projects || [],
						purchaseTypes: Array.from(
							new Set([...(filterData.purchaseTypes || []), "Z", "NRV"]),
						).sort(),
						purchaseTypeSystems: filterData.purchaseTypeSystems || [],
						seTeams: filterData.seTeams || [],
						rampUpGroups: filterData.rampUpGroups || [],
						flags: filterData.flags || [],
						aenderungsnummers: filterData.aenderungsnummers || [],
						changeDescriptions: filterData.changeDescriptions || [],
						fets: filterData.fets || [],
						depts: filterData.depts || [],
						// dynamic-filtering mappings
						projectPurchaseTypeMapping:
							filterData.projectPurchaseTypeMapping || {},
						projectSeTeamsMapping: filterData.projectSeTeamsMapping || {},
						projectRampUpGroupMapping:
							filterData.projectRampUpGroupMapping || {},
						projectAenderungsnummerMapping:
							filterData.projectAenderungsnummerMapping || {},
						projectChangeDescriptionMapping:
							filterData.projectChangeDescriptionMapping || {},
						projectDeptMapping: filterData.projectDeptMapping || {},
						programmPurchaseTypeMapping:
							filterData.programmPurchaseTypeMapping || {},
						programmSeTeamsMapping: filterData.programmSeTeamsMapping || {},
						programmProjectMapping: filterData.programmProjectMapping || {},
						programmRampUpGroupMapping:
							filterData.programmRampUpGroupMapping || {},
						programmAenderungsnummerMapping:
							filterData.programmAenderungsnummerMapping || {},
						programmChangeDescriptionMapping:
							filterData.programmChangeDescriptionMapping || {},
						programmDeptMapping: filterData.programmDeptMapping || {},
					});
					setEditOptions({
						projects: filterData.projects || [],
						purchaseTypes: Array.from(
							new Set([...(filterData.purchaseTypes || []), "Z", "NRV"]),
						).sort(),
						purchaseTypeSystems: [],
						total_req: [],
						seTeams: filterData.seTeams || [],
						gxClassifications: [],
						rampUpGroups: filterData.rampUpGroups || [],
						bemiErf: [],
						suppliers: filterData.suppliers || [],
					});
				})
				.catch((error) => {
					console.error(
						"Error reloading PTSachNr filter options for view type:",
						newViewType,
						error,
					);
				});

			// Fetch data with current filters, reset to page 1, force refresh to bypass cache
			// This ensures the view type change always loads data
			if (dataTableRef.current?.fetchData) {
				dataTableRef.current.fetchData(
					currentFilters,
					1,
					defaultItemsPerPage,
					newViewType,
					true,
				);
			}
		}
	};
	const handleClearAllFilters = async () => {
		// Set clearing state to immediately update active filters display
		setIsFilterClearing(true);

		// Reset all filter states to their initial empty values
		setSelectedProject([]);
		setSelectedPurchaseType([]);
		setSelectedPurchaseTypeSystem([]);
		setSelectedProgramm([]);
		setSelectedSeTeams([]);
		setSelectedGxClassification([]);
		setSelectedRampUpGroup([]);
		setSelectedBemiErf([]);
		setSelectedFlag([]);
		setSelectedAenderungsnummer([]);
		setSelectedChangeDescription([]);
		setSelectedFet({ from: "", to: "" });
		setSelectedDept([]);
		setSelectedPartNoPrefix([]);
		setShowMismatchOnly(false);
		setSearchInput("");

		// Reset items per page to 100 (Prototype default) when clearing all filters
		setItemsPerPage(100); // Use setTimeout to ensure state updates are processed before fetching data
		setTimeout(() => {
			if (dataTableRef.current && dataTableRef.current.fetchData) {
				// Force refresh to bypass cache and ensure API request is generated
				dataTableRef.current.fetchData({}, 1, 100, "Prototype", true);
			}
			// Reset clearing state after data fetch
			setIsFilterClearing(false);
		}, 0);
	};
	const handleItemsPerPageChange = (newItemsPerPage) => {
		// Use the new items per page value passed in
		// Only proceed if the page size actually changed
		if (newItemsPerPage === itemsPerPage) return;
		const currentFilters = {
			project: selectedProject,
			purchaseType: selectedPurchaseType,
			purchaseTypeSystem: selectedPurchaseTypeSystem,
			programm: selectedProgramm,
			seTeams: selectedSeTeams,
			gxClassification: selectedGxClassification,
			rampUpGroup: selectedRampUpGroup,
			aenderungsnummer: selectedAenderungsnummer,
			changeDescription: selectedChangeDescription,
			fet: selectedFet,
			dept: selectedDept,
			partNoPrefix: selectedPartNoPrefix,
			bemiErf: selectedBemiErf,
			flag: selectedFlag,
			search: searchQuery,
		};

		// Update the items per page and fetch data in a single update
		setItemsPerPage(newItemsPerPage);

		// Use a small timeout to ensure state is updated before making the request
		setTimeout(() => {
			if (dataTableRef.current?.fetchData) {
				dataTableRef.current.fetchData(
					currentFilters,
					1,
					newItemsPerPage,
					selectedViewType,
				);
			}
		}, 0);
	};

	const BulkEditModal = ({ editOptions }) => {
		const [dropdownValues, setDropdownValues] = useState({
			se_teams: [],
			purchase_type_system: [],
			purchase_type: [],
			supplier: [],
		}); // Load unique values when modal opens
		useEffect(() => {
			if (isBulkEditOpen && editOptions) {
				// For the VFF view type, "M" is a valid Purchase Type selection even
				// though it may not yet exist in the current VFF dataset, so make sure
				// it is offered in the bulk-edit dropdown. The list is kept sorted so
				// ordering stays consistent (H, K, M, N, NRV, Z). Other view types
				// (Prototype, Series) keep their existing options unchanged.
				const purchaseTypeOptions =
					selectedViewType === "VFF"
						? Array.from(
								new Set([...(editOptions.purchaseTypes || []), "M"]),
							).sort()
						: editOptions.purchaseTypes || [];
				setDropdownValues({
					se_teams: editOptions.seTeams || [],
					purchase_type_system: editOptions.purchaseTypeSystems || [],
					purchase_type: purchaseTypeOptions,
					supplier: editOptions.suppliers || [],
				});
			}
		}, [isBulkEditOpen, editOptions, selectedViewType]);

		// Clean up search timeout when modal closes
		useEffect(() => {
			if (!isBulkEditOpen) {
				clearTimeout(window.searchTimeout);
				setSearchQuery("");
				setSearchResults([]);
				setShowSearchResults(false);
			}
		}, [isBulkEditOpen]);

		// Update modal selected items when selectedItems changes
		useEffect(() => {
			setModalSelectedItems([...selectedItems]);
		}, [selectedItems]);
		const [modalSelectedItems, setModalSelectedItems] = useState([
			...selectedItems,
		]);
		const [searchResults, setSearchResults] = useState([]);
		const [searchQuery, setSearchQuery] = useState("");
		const [isSearching, setIsSearching] = useState(false);
		const [showSearchResults, setShowSearchResults] = useState(false);
		const [bulkEditData, setBulkEditData] = useState({
			se_teams: "",
			purchase_type_system: "",
			purchase_type: "",
			supplier: "",
			total_req: "", // Add total_req to the state (for Prototype)
			series_req: "", // Add series_req to the state (for VFF/Series)
			tool_req: "", // Add tool_req to the state (for VFF)
			flag: "", // Add flag/status to the state
			flagging: "", // Add flagging/flag-icon to the state
		});
		const [bulkComment, setBulkComment] = useState("");
		const [isSaving, setIsSaving] = useState(false);
		const [bulkSeTeamShowManual, setBulkSeTeamShowManual] = useState(false);
		const [bulkSeTeamShowTooltip, setBulkSeTeamShowTooltip] = useState(false);

		const handleBulkEditChange = (field, value) => {
			setBulkEditData((prev) => ({
				...prev,
				[field]: value,
			}));

			// Update all selected items immediately when dropdown value changes
			if (value !== "") {
				const updatedItems = modalSelectedItems.map((item) => ({
					...item,
					[field]: value,
				}));
				setModalSelectedItems(updatedItems);
			}
		};

		// Search functionality for finding items to add
		const handleSearchItems = async (query) => {
			if (!query.trim()) {
				setSearchResults([]);
				setShowSearchResults(false);
				return;
			}

			setIsSearching(true);
			setShowSearchResults(true);

			try {
				// Use the same search endpoint as the main table
				const searchPayload = {
					project: selectedProject || [],
					programm: selectedProgramm || [],
					purchase_type_system: [],
					purchase_type: selectedPurchaseType || [],
					se_teams: selectedSeTeams || [],
					gx_classification: selectedGxClassification || [],
					ramp_up_group: selectedRampUpGroup || [],
					bemi_erf: selectedBemiErf || [],
					flag: selectedFlag || [],
					supplier: [],
					aenderungsnummer: selectedAenderungsnummer || [],
					change_description: selectedChangeDescription || [],
					fet_from: selectedFet?.from || "",
					fet_to: selectedFet?.to || "",
					dept: selectedDept || [],
					part_no_prefix: selectedPartNoPrefix || [],
					search: query,
					page: 1,
					items_per_page: 50, // Limit search results
					view_type:
						selectedViewType === "Both" ? "Combined" : selectedViewType,
				};

				const response = await apiClient.post(
					"/api/filtered-data",
					searchPayload,
				);

				const result = response.data;
				setSearchResults(result.data || []);
			} catch (error) {
				console.error("Error searching items:", error);
				setSearchResults([]);
			} finally {
				setIsSearching(false);
			}
		};

		const handleSearchInputChange = (e) => {
			const query = e.target.value;
			setSearchQuery(query);

			// Debounce search
			clearTimeout(window.searchTimeout);
			window.searchTimeout = setTimeout(() => {
				handleSearchItems(query);
			}, 300);
		};

		const handleClearSearch = () => {
			setSearchQuery("");
			setSearchResults([]);
			setShowSearchResults(false);
			clearTimeout(window.searchTimeout);
		};
		const handleModalSelectItem = (item) => {
			// Use composite key (snr + ptsnr + project) to uniquely identify each row,
			// preventing phantom multi-selection when rows share the same snr_kurz.
			const getKey = (i) =>
				`${i.snr_kurz || i.snr || ""}-${i.pt_snr || i.ptsnr || ""}-${i.projekt || i.project || ""}`;
			const itemKey = getKey(item);
			const isSelected = modalSelectedItems.some(
				(selected) => getKey(selected) === itemKey,
			);

			if (isSelected) {
				setModalSelectedItems(
					modalSelectedItems.filter((selected) => getKey(selected) !== itemKey),
				);
			} else {
				if (
					!modalSelectedItems.some((selected) => getKey(selected) === itemKey)
				) {
					setModalSelectedItems([...modalSelectedItems, item]);
				}
			}
		};
		const handleSaveWithItems = async () => {
			try {
				// Filter out empty values
				const updates = Object.fromEntries(
					Object.entries(bulkEditData).filter(([_, v]) => v !== ""),
				);

				// __clear__ is the sentinel for "set to NULL" — translate __none__ alias too
				if (updates.flag === "__none__") {
					updates.flag = "__clear__";
				}

				const hasFieldUpdates = Object.keys(updates).length > 0;
				const hasComment = bulkComment.trim().length > 0;

				if (!hasFieldUpdates && !hasComment) {
					alert("Please make a change or add a comment before saving");
					return;
				}

				if (modalSelectedItems.length === 0) {
					alert("No items selected for bulk edit");
					return;
				}

				// Show confirmation dialog
				// const confirmed = window.confirm(
				//   `Are you sure you want to update ${modalSelectedItems.length} item(s) with the selected changes?`
				// );

				// if (!confirmed) return;        // Start loading state
				setIsSaving(true);

				// Only run the field bulk-update if there are field changes
				if (hasFieldUpdates) {
					// Prepare the bulk update request
					const bulkUpdateRequest = {
						items: modalSelectedItems.map((item) => ({
							snr: item.snr_kurz || item.snr || "",
							ptsnr: item.ptsnr || item.pt_snr || "",
							project: item.project || item.projekt || "",
						})),
						updates: updates,
						view_type: selectedViewType,
					};

					console.log("📝 Sending bulk update request:", bulkUpdateRequest);
					const response = await apiClient.put(
						"/api/bulk-update-ptsachnr-data",
						bulkUpdateRequest,
					);
					console.log("✅ Bulk update response:", response.data);

					// Update the table data to reflect changes immediately
					if (dataTableRef.current && dataTableRef.current.updateLocalData) {
						dataTableRef.current.updateLocalData(modalSelectedItems, updates);
					}
					// NOTE: auto-comments for BY SET (purchase_type) and SET Allocation
					// (se_teams) changes are generated on the backend inside
					// /api/bulk-update-ptsachnr-data, where the old values and exact
					// records/category are known for certain.
				} // Post one comment to all selected items in a single request
				if (hasComment) {
					const currentUser =
						getUserDisplayName() || localStorage.getItem("username") || "User";
					await apiClient.post("/api/bulk-comments", {
						items: modalSelectedItems.map((item) => ({
							snr: item.ptsnr || item.pt_snr || item.snr_kurz || item.snr || "",
							category: item.category || selectedViewType || "Prototype",
						})),
						text: bulkComment.trim(),
						user: currentUser,
					});
					console.log(
						`✅ Bulk comment added to ${modalSelectedItems.length} item(s) in one request`,
					);
				}

				// ── Patch rows in-place — scroll position fully preserved ─────────────
				// updateLocalData was already called above for field changes.
				// Clear selections without touching the loaded data or scroll position.
				setSelectedItems([]);
				setAllSelected(false);
				if (dataTableRef.current && dataTableRef.current.clearSelections) {
					dataTableRef.current.clearSelections();
				}

				console.log(
					`✅ PTSachNr bulk edit: ${modalSelectedItems.length} items patched in-place, scroll preserved.`,
				);

				// Show success message
				// setTimeout(() => {
				//   alert(`✅ Success!\n\nUpdated ${result.items_updated} item(s) with ${result.updated_fields.length} field(s):\n• ${result.updated_fields.join('\n• ')}\n\nChanges have been saved successfully.`);
				// }, 100);        // Close modal and reset form
				setBulkEditData({
					se_teams: "",
					purchase_type_system: "",
					gx_classification: "",
					supplier: "",
					total_req: "",
					series_req: "",
					tool_req: "",
					flag: "",
					flagging: "",
				});
				setBulkComment("");
				setModalSelectedItems([]);
				handleCloseBulkEdit();
			} catch (error) {
				console.error("Error saving bulk edit:", error);
				if (error?.response?.status === 403) {
					setShowBulkAccessDeniedModal(true);
				} else {
					alert(`Failed to save changes: ${error.message || "Unknown error"}`);
				}
			} finally {
				// End loading state
				setIsSaving(false);
			}
		};

		if (!isBulkEditOpen) return null;

		return createPortal(
			<div style={{ ...styles.modal, ...fontStyles.regular }}>
				<div
					style={{
						...styles.modalContent,
						width: "90%",
						maxWidth: "1200px",
						position: "relative",
					}}
				>
					<div style={styles.modalHeader}>
						<h2
							style={{ ...styles.modalTitle, fontSize: "18px", color: "#333" }}
						>
							Bulk Edit ({modalSelectedItems.length} items selected)
						</h2>
						<button
							onClick={handleCloseBulkEdit}
							style={{
								background: "none",
								border: "none",
								fontSize: "30px",
								cursor: "pointer",
								color: "#666",
								padding: "5px 10px",
								borderRadius: "4px",
								":hover": {
									backgroundColor: "#f0f0f0",
								},
							}}
						>
							×
						</button>
					</div>

					<div style={styles.modalBody}>
						{/* Selected Items Table */}
						<div style={{ marginBottom: "20px" }}>
							<h3
								style={{
									margin: "0 0 15px 0",
									fontSize: "14px",
									color: "#444",
									fontWeight: "500",
								}}
							>
								Selected Items ({modalSelectedItems.length})
							</h3>
							{modalSelectedItems.length > 0 ? (
								<div
									style={{
										maxHeight: "300px",
										overflowY: "auto",
										border: "1px solid #eee",
										borderRadius: "4px",
									}}
								>
									<table style={{ ...styles.table, width: "100%" }}>
										<thead>
											<tr style={{ backgroundColor: "#f5f5f5" }}>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Part No.
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "160px",
													}}
												>
													Description
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "100px",
													}}
												>
													Project
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													SE Team
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Purchase Type
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "120px",
													}}
												>
													Purchase Type System
												</th>
												{selectedViewType === "Prototype" && (
													<th
														style={{
															...styles.tableHeader,
															padding: "10px",
															textAlign: "left",
															minWidth: "80px",
														}}
													>
														Total Req
													</th>
												)}
												{(selectedViewType === "VFF" ||
													selectedViewType === "Series") && (
													<th
														style={{
															...styles.tableHeader,
															padding: "10px",
															textAlign: "left",
															minWidth: "80px",
														}}
													>
														Tool Req
													</th>
												)}
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "80px",
													}}
												>
													Status
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "80px",
													}}
												>
													Flagging
												</th>
												<th
													style={{
														...styles.tableHeader,
														padding: "10px",
														textAlign: "left",
														minWidth: "100px",
													}}
												>
													Action
												</th>
											</tr>
										</thead>
										<tbody>
											{modalSelectedItems.map((item, index) => (
												<tr
													key={`${item.snr || item.id}-${index}`}
													style={{
														borderBottom: "1px solid #eee",
														":hover": { backgroundColor: "#f9f9f9" },
													}}
												>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.ptsnr || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.description || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.project || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.se_teams || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.purchase_type || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.purchase_type_system || "N/A"}
													</td>
													{selectedViewType === "Prototype" && (
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.total_req || "N/A"}
														</td>
													)}
													{(selectedViewType === "VFF" ||
														selectedViewType === "Series") && (
														<td
															style={{
																...styles.tableCell,
																padding: "10px",
																fontSize: "12px",
															}}
														>
															{item.tool_req || "N/A"}
														</td>
													)}
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.flag || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														{item.flagging || "N/A"}
													</td>
													<td
														style={{
															...styles.tableCell,
															padding: "10px",
															fontSize: "12px",
														}}
													>
														<button
															onClick={() => {
																setModalSelectedItems(
																	modalSelectedItems.filter(
																		(_, i) => i !== index,
																	),
																);
															}}
															style={{
																background: "#cc0000",
																color: "white",
																border: "none",
																borderRadius: "4px",
																padding: "4px 8px",
																cursor: "pointer",
																fontSize: "12px",
																":hover": {
																	backgroundColor: "#990000",
																},
															}}
														>
															Remove
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<div
									style={{
										padding: "20px",
										textAlign: "center",
										color: "#666",
										backgroundColor: "#f9f9f9",
										border: "1px dashed #ddd",
										borderRadius: "4px",
									}}
								>
									No items selected. Use the table to select items for bulk
									edit.
								</div>
							)}
						</div>

						{/* Search Section */}
						<div style={{ marginBottom: "20px" }}>
							<h3
								style={{
									margin: "0 0 15px 0",
									fontSize: "14px",
									color: "#444",
									fontWeight: "500",
								}}
							>
								Search & Add Items
							</h3>

							{/* Search Input */}
							<div style={{ position: "relative", marginBottom: "15px" }}>
								<input
									type="text"
									placeholder="Search for PNR, SNR, Description to add items..."
									value={searchQuery}
									onChange={handleSearchInputChange}
									style={{
										width: "100%",
										padding: "10px 40px 10px 40px",
										border: "1px solid #ddd",
										borderRadius: "4px",
										fontSize: "14px",
										boxSizing: "border-box",
										boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
									}}
								/>
								<Search
									style={{
										position: "absolute",
										left: "12px",
										top: "50%",
										transform: "translateY(-50%)",
										color: "#666",
										width: "16px",
										height: "16px",
										pointerEvents: "none",
									}}
								/>
								{searchQuery && (
									<button
										type="button"
										onClick={handleClearSearch}
										style={{
											position: "absolute",
											right: "12px",
											top: "50%",
											transform: "translateY(-50%)",
											background: "none",
											border: "none",
											color: "#999",
											cursor: "pointer",
											padding: "4px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: "18px",
											lineHeight: 1,
										}}
										aria-label="Clear search"
									>
										×
									</button>
								)}
							</div>

							{/* Search Results */}
							{showSearchResults && (
								<div
									style={{
										border: "1px solid #e0e0e0",
										borderRadius: "4px",
										overflow: "hidden",
										boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
										marginBottom: "15px",
									}}
								>
									<div
										style={{
											backgroundColor: "#f5f5f5",
											padding: "10px 15px",
											borderBottom: "1px solid #e0e0e0",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<h4
											style={{
												margin: 0,
												fontSize: "13px",
												color: "#333",
												fontWeight: "500",
											}}
										>
											Search Results
										</h4>
										<div style={{ fontSize: "12px", color: "#666" }}>
											{isSearching
												? "Searching..."
												: `${searchResults.length} items found`}
											{searchResults.length > 0 &&
												modalSelectedItems.filter((item) =>
													searchResults.some(
														(result) =>
															(result.snr || result.id) ===
															(item.snr || item.id),
													),
												).length > 0 && (
													<span
														style={{
															marginLeft: "10px",
															color: "black",
															fontWeight: "bold",
														}}
													>
														(
														{
															modalSelectedItems.filter((item) =>
																searchResults.some(
																	(result) =>
																		(result.snr || result.id) ===
																		(item.snr || item.id),
																),
															).length
														}{" "}
														selected)
													</span>
												)}
										</div>
									</div>

									<div style={{ maxHeight: "300px", overflowY: "auto" }}>
										{isSearching ? (
											<Box display="flex" justifyContent="center" padding={4}>
												<CircularProgress size={24} sx={{ color: "#cc0000" }} />
											</Box>
										) : searchResults.length > 0 ? (
											<table
												style={{
													...styles.table,
													width: "100%",
													borderCollapse: "collapse",
												}}
											>
												<thead>
													<tr style={{ backgroundColor: "#f9f9f9" }}>
														<th
															style={{
																...styles.tableHeader,
																width: "40px",
																padding: "8px",
															}}
														></th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Part No.
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Description
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Project
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															SE Team
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															Purchase Type
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
															}}
														>
															GX Classification
														</th>
														<th
															style={{
																...styles.tableHeader,
																padding: "8px",
																textAlign: "left",
																width: "80px",
															}}
														>
															Action
														</th>
													</tr>
												</thead>
												<tbody>
													{searchResults.map((item) => {
														const isAlreadySelected = modalSelectedItems.some(
															(selected) =>
																(selected.snr || selected.id) ===
																(item.snr || item.id),
														);
														return (
															<tr
																key={item.snr || item.id}
																style={{
																	borderBottom: "1px solid #eee",
																	backgroundColor: isAlreadySelected
																		? "#f0f8ff"
																		: "transparent",
																}}
															>
																<td
																	style={{
																		...styles.tableCell,
																		textAlign: "center",
																		padding: "8px",
																	}}
																>
																	<input
																		type="checkbox"
																		checked={isAlreadySelected}
																		onChange={() => handleModalSelectItem(item)}
																		style={{ cursor: "pointer" }}
																	/>
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.ptsnr || item.snr || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																		maxWidth: "200px",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																	}}
																>
																	{item.description || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.project || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.se_teams || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.purchase_type || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																	}}
																>
																	{item.gx_classification || "N/A"}
																</td>
																<td
																	style={{
																		...styles.tableCell,
																		fontSize: "12px",
																		padding: "8px",
																		textAlign: "center",
																	}}
																>
																	{!isAlreadySelected ? (
																		<button
																			onClick={() =>
																				handleModalSelectItem(item)
																			}
																			style={{
																				backgroundColor: "#cc0000",
																				color: "white",
																				border: "none",
																				borderRadius: "4px",
																				padding: "4px 8px",
																				cursor: "pointer",
																				fontSize: "11px",
																				":hover": {
																					backgroundColor: "#cc0000",
																				},
																			}}
																		>
																			Add
																		</button>
																	) : (
																		<span
																			style={{
																				fontSize: "11px",
																				color: "black",
																				fontWeight: "bold",
																			}}
																		>
																			✓ Added
																		</span>
																	)}
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										) : (
											<Box padding={2} textAlign="center" color="#666">
												{searchQuery
													? "No items found matching your search"
													: "Enter search terms to find items"}
											</Box>
										)}
									</div>
								</div>
							)}
						</div>

						<div style={{ marginBottom: "20px" }}>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: "15px",
								}}
							>
								<div>
									{" "}
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										SET Allocation
									</label>
									<select
										style={styles.select}
										value={
											bulkSeTeamShowManual
												? "__manual__"
												: bulkEditData.se_teams
										}
										onChange={(e) => {
											if (e.target.value === "__manual__") {
												setBulkSeTeamShowManual(true);
												handleBulkEditChange("se_teams", "");
											} else {
												setBulkSeTeamShowManual(false);
												setBulkSeTeamShowTooltip(false);
												handleBulkEditChange("se_teams", e.target.value);
											}
										}}
									>
										{" "}
										<option value="">Keep existing</option>
										<option value="__manual__">Other (type manually…)</option>
										{dropdownValues.se_teams.map((team) => (
											<option key={team} value={team}>
												{team}
											</option>
										))}
									</select>
									{bulkSeTeamShowManual && (
										<div style={{ position: "relative", marginTop: "6px" }}>
											<input
												type="text"
												placeholder="Type SE Team…"
												value={bulkEditData.se_teams}
												style={{
													width: "100%",
													boxSizing: "border-box",
													padding: "6px 8px",
													fontSize: "13px",
													border: "1.5px solid #e53935",
													borderRadius: "3px",
													outline: "none",
													backgroundColor: "#fff",
													color: "#333",
												}}
												onChange={(e) => {
													handleBulkEditChange("se_teams", e.target.value);
													setBulkSeTeamShowTooltip(e.target.value.length > 0);
												}}
												onBlur={() => setBulkSeTeamShowTooltip(false)}
												onFocus={() =>
													setBulkSeTeamShowTooltip(
														bulkEditData.se_teams.length > 0,
													)
												}
											/>
											{bulkSeTeamShowTooltip && (
												<div
													style={{
														position: "absolute",
														bottom: "calc(100% + 8px)",
														left: "0",
														backgroundColor: "#1e2832",
														color: "#fff",
														padding: "7px 10px",
														borderRadius: "4px",
														fontSize: "11px",
														lineHeight: "1.4",
														whiteSpace: "normal",
														maxWidth: "260px",
														zIndex: 9999,
														pointerEvents: "none",
														boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
													}}
												>
													As you are manually adding an SE-Team, please refresh
													the page to see the value in the dropdown for other
													records.
													<div
														style={{
															position: "absolute",
															top: "100%",
															left: "14px",
															borderWidth: "5px",
															borderStyle: "solid",
															borderColor:
																"#1e2832 transparent transparent transparent",
														}}
													/>
												</div>
											)}
										</div>
									)}
								</div>{" "}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Purchase Type
									</label>
									<select
										style={styles.select}
										value={bulkEditData.purchase_type}
										onChange={(e) =>
											handleBulkEditChange("purchase_type", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.purchase_type.map((type) => (
											<option key={type} value={type}>
												{type}
											</option>
										))}
									</select>
								</div>
								{/* Total Req Field - Only for Prototype */}
								{selectedViewType === "Prototype" && (
									<div>
										<label
											style={{
												display: "block",
												marginBottom: "8px",
												fontSize: "12px",
												color: "#666",
											}}
										>
											Series Annual Req
										</label>
										<input
											type="number"
											style={{
												...styles.select,
												appearance: "none",
												WebkitAppearance: "none",
												MozAppearance: "textfield", // removes arrows in Firefox
												padding: "8px",
												fontSize: "14px",
												borderRadius: "4px",
												backgroundColor: "#fff",
												color: "#333",
												borderRadius: "0px",
												width: "97%",
												height: "18px",
											}}
											value={bulkEditData.total_req}
											onChange={(e) =>
												handleBulkEditChange("total_req", e.target.value)
											}
											placeholder="Keep existing"
										/>
									</div>
								)}{" "}
								{/* Tool Req Field - Only for VFF and Series */}
								{(selectedViewType === "VFF" ||
									selectedViewType === "Series") && (
									<div>
										<label
											style={{
												display: "block",
												marginBottom: "8px",
												fontSize: "12px",
												color: "#666",
											}}
										>
											Tool Req
										</label>
										<select
											style={{
												...styles.select,
												padding: "8px",
												fontSize: "14px",
												borderRadius: "4px",
												backgroundColor: "#fff",
												color: "#333",
												borderRadius: "0px",
												width: "100%",
												height: "34px",
												cursor: "pointer",
											}}
											value={bulkEditData.tool_req || ""}
											onChange={(e) =>
												handleBulkEditChange("tool_req", e.target.value)
											}
										>
											<option value="">Keep existing</option>
											<option value="Yes">Yes</option>
											<option value="No">No</option>
											<option value="N/A">-</option>
										</select>
									</div>
								)}
								{/* Series Req Field - Only for VFF and Series */}
								{(selectedViewType === "VFF" ||
									selectedViewType === "Series") && (
									<div>
										<label
											style={{
												display: "block",
												marginBottom: "8px",
												fontSize: "12px",
												color: "#666",
											}}
										>
											Series Annual Req
										</label>
										<input
											type="number"
											style={{
												...styles.select,
												appearance: "none",
												WebkitAppearance: "none",
												MozAppearance: "textfield",
												padding: "8px",
												fontSize: "14px",
												backgroundColor: "#fff",
												color: "#333",
												borderRadius: "0px",
												width: "97%",
												height: "18px",
											}}
											value={bulkEditData.series_req}
											onChange={(e) =>
												handleBulkEditChange("series_req", e.target.value)
											}
											placeholder="Keep existing"
										/>
									</div>
								)}
								{/* Supplier Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Supplier
									</label>
									<select
										style={styles.select}
										value={bulkEditData.supplier}
										onChange={(e) =>
											handleBulkEditChange("supplier", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										{dropdownValues.supplier.map((supplier) => (
											<option key={supplier} value={supplier}>
												{supplier}
											</option>
										))}
									</select>{" "}
								</div>{" "}
								{/* Status Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
											fontFamily:'MAN Europe_Bold'
										}}
									>
										Status
									</label>
									<select
										style={styles.select}
										value={bulkEditData.flag}
										onChange={(e) =>
											handleBulkEditChange("flag", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										<option value="__none__">None (clear)</option>
										<option value="in Arbeit">in Arbeit</option>
										<option value="erledigt">erledigt</option>
									</select>
								</div>
								{/* Flagging Field */}
								<div>
									<label
										style={{
											display: "block",
											marginBottom: "8px",
											fontSize: "12px",
											color: "#666",
										}}
									>
										Flagging
									</label>
									<select
										style={styles.select}
										value={bulkEditData.flagging}
										onChange={(e) =>
											handleBulkEditChange("flagging", e.target.value)
										}
									>
										<option value="">Keep existing</option>
										<option value="Y">Flagged (Y)</option>
										<option value="N">Not Flagged (N)</option>
									</select>
								</div>
							</div>

							{/* Comment Field — spans full width below the grid */}
							<div style={{ marginTop: "16px" }}>
								<label
									style={{
										display: "block",
										marginBottom: "8px",
										fontSize: "12px",
										color: "#666",
									}}
								>
									Comment
								</label>
								<textarea
									rows={3}
									placeholder="Type a comment to add to all selected items…"
									value={bulkComment}
									onChange={(e) => setBulkComment(e.target.value)}
									style={{
										width: "100%",
										boxSizing: "border-box",
										padding: "8px 10px",
										fontSize: "13px",
										border: "1px solid #ddd",
										borderRadius: "3px",
										resize: "vertical",
										fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
										color: "#333",
										backgroundColor: "#fff",
										outline: "none",
										lineHeight: "1.5",
									}}
								/>
							</div>
						</div>
					</div>
					<div style={styles.modalFooter}>
						<button
							onClick={handleCloseBulkEdit}
							style={{
								...styles.button,
								backgroundColor: "#fff",
								color: "#666",
								border: "1px solid #ddd",
							}}
							disabled={isSaving}
						>
							Cancel
						</button>
						<button
							onClick={handleSaveWithItems}
							style={{
								...styles.button,
								backgroundColor: "#cc0000",
								opacity: isSaving ? 0.7 : 1,
							}}
							disabled={isSaving}
						>
							{isSaving ? "Saving..." : "Save bulk edit"}
						</button>
					</div>

					{/* Loading Overlay */}
					{isSaving && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								zIndex: 1001,
								borderRadius: "8px",
							}}
						>
							<div
								style={{
									width: "40px",
									height: "40px",
									border: "4px solid #f3f3f3",
									borderTop: "4px solid #cc0000",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									marginBottom: "20px",
								}}
							></div>
							<div
								style={{
									fontSize: "16px",
									fontWeight: "500",
									color: "#333",
									marginBottom: "8px",
								}}
							>
								Updating {modalSelectedItems.length} item(s)...
							</div>
							<div
								style={{
									fontSize: "14px",
									color: "#666",
								}}
							>
								Please wait while we save your changes
							</div>
						</div>
					)}
				</div>
			</div>,
			document.body,
		);
	};

	return (
		<div style={styles.mainContainer}>
			{/* Loading overlay for dashboard filter application */}
			{isDashboardFilterLoading && (
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
						flexDirection: "column",
					}}
				>
					<CircularProgress
						size={80}
						sx={{ color: "#1e2832", marginBottom: 2 }}
					/>
					<Typography
						variant="h6"
						sx={{
							color: "white",
							fontFamily: "MAN Europe_Bold",
							textAlign: "center",
							maxWidth: "300px",
						}}
					>
						Loading Supply Type Data...
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.8)",
							fontFamily: "MAN Europe_Regular",
							textAlign: "center",
							marginTop: 1,
						}}
					>
						Please wait while we load your supply type data
					</Typography>
				</div>
			)}
			{/* Add CSS for spinner animation */}
			<style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
			<Header user={user} signOut={signOut} onStartTour={handleStartTour} />
			<div style={styles.contentWrapper}>
				<PTSachNrSidebar
					onFiltersChange={handleFiltersChange}
					onClearFilters={handleClearFilters}
					onColumnVisibilityChange={handleColumnVisibilityChange}
					columnVisibility={columnVisibility}
					selectedViewType={selectedViewType}
					onViewTypeChange={handleViewTypeChange}
					isLoading={isFilterOptionsLoading}
					error={filterOptionsError}
					filterOptions={{
						programms: filterOptions.programms || [],
						projects: filterOptions.projects || [],
						purchaseTypes: filterOptions.purchaseTypes || [],
						purchaseTypeSystems: filterOptions.purchaseTypeSystems || [],
						seTeams: filterOptions.seTeams || [],
						rampUpGroups: filterOptions.rampUpGroups || [],
						flags: filterOptions.flags || [],
						aenderungsnummers: filterOptions.aenderungsnummers || [],
						changeDescriptions: filterOptions.changeDescriptions || [],
						fets: filterOptions.fets || [],
						depts: filterOptions.depts || [],
						// dynamic-filtering mappings
						projectPurchaseTypeMapping:
							filterOptions.projectPurchaseTypeMapping || {},
						projectSeTeamsMapping: filterOptions.projectSeTeamsMapping || {},
						projectRampUpGroupMapping:
							filterOptions.projectRampUpGroupMapping || {},
						projectAenderungsnummerMapping:
							filterOptions.projectAenderungsnummerMapping || {},
						projectChangeDescriptionMapping:
							filterOptions.projectChangeDescriptionMapping || {},
						projectDeptMapping: filterOptions.projectDeptMapping || {},
						programmPurchaseTypeMapping:
							filterOptions.programmPurchaseTypeMapping || {},
						programmSeTeamsMapping: filterOptions.programmSeTeamsMapping || {},
						programmProjectMapping: filterOptions.programmProjectMapping || {},
						programmRampUpGroupMapping:
							filterOptions.programmRampUpGroupMapping || {},
						programmAenderungsnummerMapping:
							filterOptions.programmAenderungsnummerMapping || {},
						programmChangeDescriptionMapping:
							filterOptions.programmChangeDescriptionMapping || {},
						programmDeptMapping: filterOptions.programmDeptMapping || {},
					}}
				/>
				<div style={styles.mainContent}>
					<div style={styles.searchContainer}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								marginBottom: "-15px",
								marginTop: "-5px",
								padding: "0 40px",
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									width: "100%",
								}}
							>
								<div
									style={{ display: "flex", alignItems: "center", gap: "10px" }}
								>
									<Home size={20} color="#666" />
									<span
										style={{ color: "#666", fontFamily: "MAN Europe_Bold" }}
									>
										Home
									</span>
									<ChevronRight size={16} color="#666" />
									<span style={{ fontFamily: "MAN Europe_Bold" }}>
										Supply type allocation
									</span>
								</div>{" "}
								<div style={{ display: "flex", gap: "10px" }}>
									<div
										style={{
											display: "flex",
											gap: "10px",
											alignItems: "center",
										}}
									>
										<CraftButton
											purpose="standard"
											variant="secondary"
											size="s"
											type="button"
											icon-name="mail"
											onClick={handleOpenPTSEmailExportModal}
										>
											Email Export
										</CraftButton>

										{/* Conditionally render bulk edit button based on view type and control flag */}
										{(selectedViewType !== "Both" ||
											ENABLE_BULK_EDIT_FOR_BOTH_VIEW) && (
											<CraftButton
												purpose="standard"
												variant="primary"
												size="s"
												type="button"
												icon-name="edit"
												disabled={
													!selectedItems ||
													selectedItems.length < 1 ||
													selectedItems.length > 100
												}
												title={
													!selectedItems || selectedItems.length < 1
														? "Select at least 1 item to bulk edit"
														: selectedItems.length > 100
															? `Bulk edit is limited to 100 records. You have selected ${selectedItems.length}.`
															: `Bulk edit ${selectedItems.length} selected items`
												}
												onClick={handleBulkEdit}
												className="bulk-edit-button"
											>
												Bulk Edit
												{selectedItems && selectedItems.length > 0
													? ` (${selectedItems.length}${selectedItems.length > 100 ? " — max 100" : ""})`
													: ""}
											</CraftButton>
										)}

										<CraftButton
											purpose="standard"
											variant="primary"
											size="s"
											type="button"
											icon-name="chevron-right"
											onClick={navigateToDashboard}
										>
											Dashboard
										</CraftButton>
									</div>
								</div>
							</div>
						</div>

						{/* Active Filters Display - Positioned exactly below Export and Bulk Edit buttons, right-aligned */}
						<div
							style={{
								padding: "0px 40px 0px 20px",
								position: "relative",
								top: "25px",
								backgroundColor: "#f1f1f1",
							}}
						>
							{" "}
							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									width: "100%",
								}}
							>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "10px",
										flexWrap: "wrap",
										position: "relative",
									}}
								>
									<span
										style={{
											fontSize: "14px",
											fontWeight: "bold",
											color: "#666",
											fontFamily: "MAN EUROPE_Bold",
										}}
									>
										Active Filters:
									</span>
									{isFilterClearing ? (
										<span
											style={{
												fontSize: "12px",
												color: "#999",
												fontFamily: "MAN EUROPE_Regular",
											}}
										>
											No active filters
										</span>
									) : (
										(() => {
											const activeFilters = [];

											// ...existing code to populate activeFilters...
											if (selectedProject && selectedProject.length > 0) {
												activeFilters.push({
													label: "Project",
													values: selectedProject,
												});
											}
											if (selectedProgramm && selectedProgramm.length > 0) {
												activeFilters.push({
													label: "Project Name",
													values: selectedProgramm,
												});
											}
											if (
												selectedPurchaseType &&
												selectedPurchaseType.length > 0
											) {
												activeFilters.push({
													label: "Purchase Type",
													values: selectedPurchaseType,
												});
											}
											if (
												selectedPurchaseTypeSystem &&
												selectedPurchaseTypeSystem.length > 0
											) {
												activeFilters.push({
													label: "Purchase Type (System)",
													values: selectedPurchaseTypeSystem,
												});
											}
											if (selectedSeTeams && selectedSeTeams.length > 0) {
												activeFilters.push({
													label: "SE-Teams",
													values: selectedSeTeams,
												});
											}
											if (
												selectedGxClassification &&
												selectedGxClassification.length > 0
											) {
												activeFilters.push({
													label: "Gx Classification",
													values: selectedGxClassification,
												});
											}
											if (
												selectedRampUpGroup &&
												selectedRampUpGroup.length > 0
											) {
												activeFilters.push({
													label: "Ramp-up Group",
													values: selectedRampUpGroup,
												});
											}
											if (selectedFlag && selectedFlag.length > 0) {
												activeFilters.push({
													label: "Flag",
													values: selectedFlag,
												});
											}
											if (searchQuery && searchQuery.trim()) {
												activeFilters.push({
													label: "Search",
													values: [searchQuery],
												});
											}
											if (
												selectedAenderungsnummer &&
												selectedAenderungsnummer.length > 0
											) {
												activeFilters.push({
													label: "Aenderungsnummer",
													values: selectedAenderungsnummer,
												});
											}
											if (
												selectedChangeDescription &&
												selectedChangeDescription.length > 0
											) {
												activeFilters.push({
													label: "Change Description",
													values: selectedChangeDescription,
												});
											}
											if (selectedFet && (selectedFet.from || selectedFet.to)) {
												const fetLabel =
													selectedFet.from && selectedFet.to
														? `${selectedFet.from} – ${selectedFet.to}`
														: selectedFet.from
															? `≥ ${selectedFet.from}`
															: `≤ ${selectedFet.to}`;
												activeFilters.push({
													label: "FET",
													values: [fetLabel],
												});
											}
											if (selectedDept && selectedDept.length > 0) {
												activeFilters.push({
													label: "Dept",
													values: selectedDept,
												});
											}
											if (
												selectedPartNoPrefix &&
												selectedPartNoPrefix.length > 0
											) {
												activeFilters.push({
													label: "Part No Prefix",
													values: selectedPartNoPrefix,
												});
											}

											if (showMismatchOnly) {
												activeFilters.push({
													label: "BY SET / BY SYSTEM",
													values: ["Mismatches only"],
												});
											}

											const totalActiveFilters = activeFilters.reduce(
												(sum, filter) => sum + filter.values.length,
												0,
											);

											// Only show first filter group, and limit to 3 values
											const displayFilter =
												activeFilters.length > 0 ? activeFilters[0] : null;
											const hiddenFiltersCount = activeFilters
												.slice(1)
												.reduce((sum, filter) => sum + filter.values.length, 0);
											const hiddenValuesInFirstFilter =
												displayFilter && displayFilter.values.length > 3
													? displayFilter.values.length - 3
													: 0;
											const totalHiddenCount =
												hiddenFiltersCount + hiddenValuesInFirstFilter;

											return activeFilters.length > 0 ? (
												<>
													<span
														style={{
															fontSize: "12px",
															color: "#999",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														({totalActiveFilters} total)
													</span>
													<div
														style={{
															display: "flex",
															gap: "8px",
															flexWrap: "wrap",
															alignItems: "center",
														}}
													>
														{displayFilter &&
															displayFilter.values.length <= 3 && (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																	}}
																>
																	<span
																		style={{
																			fontSize: "11px",
																			fontWeight: "bold",
																			color: "#cc0000",
																			fontFamily: "MAN EUROPE_Regular",
																		}}
																	>
																		{displayFilter.label}:
																	</span>
																	<div
																		style={{
																			display: "flex",
																			gap: "2px",
																			flexWrap: "wrap",
																		}}
																	>
																		{displayFilter.values.map(
																			(value, valueIndex) => (
																				<span
																					key={valueIndex}
																					style={{
																						display: "inline-block",
																						backgroundColor: "#cc0000",
																						color: "white",
																						padding: "2px 6px",
																						borderRadius: "10px",
																						fontSize: "10px",
																						fontFamily: "MAN EUROPE_Regular",
																					}}
																				>
																					{value}
																				</span>
																			),
																		)}
																	</div>
																</div>
															)}{" "}
														{totalHiddenCount > 0 && (
															<button
																onClick={() =>
																	setShowFilterTooltip(!showFilterTooltip)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																	gap: "4px",
																	backgroundColor: "#666",
																	color: "white",
																	padding: "4px 10px",
																	borderRadius: "12px",
																	fontSize: "11px",
																	fontFamily: "MAN EUROPE_Regular",
																	cursor: "pointer",
																	border: "none",
																	transition: "background-color 0.2s",
																	whiteSpace: "nowrap",
																}}
																onMouseEnter={(e) =>
																	(e.target.style.backgroundColor = "#555")
																}
																onMouseLeave={(e) =>
																	(e.target.style.backgroundColor = "#666")
																}
															>
																Show more filters ({totalHiddenCount})
															</button>
														)}
													</div>

													{/* Tooltip with close button */}
													{showFilterTooltip && (
														<div
															style={{
																position: "absolute",
																top: "100%",
																right: "0",
																marginTop: "8px",
																backgroundColor: "white",
																border: "1px solid #ddd",
																borderRadius: "8px",
																padding: "12px",
																boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
																zIndex: 1000,
																minWidth: "300px",
																maxWidth: "500px",
																maxHeight: "400px",
																overflowY: "auto",
															}}
														>
															<div
																style={{
																	display: "flex",
																	justifyContent: "space-between",
																	alignItems: "center",
																	marginBottom: "8px",
																}}
															>
																<div
																	style={{
																		fontSize: "12px",
																		fontWeight: "bold",
																		color: "#333",
																		fontFamily: "MAN EUROPE_Bold",
																	}}
																>
																	All Active Filters
																</div>
																<button
																	onClick={() => setShowFilterTooltip(false)}
																	style={{
																		background: "none",
																		border: "none",
																		fontSize: "20px",
																		cursor: "pointer",
																		color: "#666",
																		padding: "0",
																		lineHeight: "1",
																		width: "20px",
																		height: "20px",
																		display: "flex",
																		alignItems: "center",
																		justifyContent: "center",
																	}}
																	aria-label="Close"
																>
																	×
																</button>
															</div>
															{activeFilters.map((filter, index) => (
																<div
																	key={index}
																	style={{
																		marginBottom: "8px",
																		paddingBottom: "8px",
																		borderBottom:
																			index < activeFilters.length - 1
																				? "1px solid #eee"
																				: "none",
																	}}
																>
																	<div
																		style={{
																			fontSize: "11px",
																			fontWeight: "bold",
																			color: "#cc0000",
																			marginBottom: "4px",
																			fontFamily: "MAN EUROPE_Bold",
																		}}
																	>
																		{filter.label}:
																	</div>
																	<div
																		style={{
																			display: "flex",
																			gap: "4px",
																			flexWrap: "wrap",
																		}}
																	>
																		{filter.values.map((value, valueIndex) => (
																			<span
																				key={valueIndex}
																				style={{
																					display: "inline-block",
																					backgroundColor: "#cc0000",
																					color: "white",
																					padding: "2px 6px",
																					borderRadius: "10px",
																					fontSize: "10px",
																					fontFamily: "MAN EUROPE_Regular",
																				}}
																			>
																				{value}
																			</span>
																		))}
																	</div>
																</div>
															))}
														</div>
													)}
												</>
											) : (
												<span
													style={{
														fontSize: "12px",
														color: "#999",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													No active filters
												</span>
											);
										})()
									)}
								</div>
							</div>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginBottom: "5px",
								padding: "0 40px",
							}}
							className="search-container"
						>
							<form
								onSubmit={handleSearchSubmit}
								style={{
									flex: 1,
									position: "relative",
									maxWidth: "600px",
									display: "flex",
									flexDirection: "column",
									gap: "8px",
								}}
							>
								<div style={{ display: "flex", gap: "8px" }}>
									<div style={{ position: "relative", flex: 1 }}>
										{" "}
										<input
											type="text"
											data-tour-target="supply-search-bar"
											placeholder="Search for PNR, SNR, Description"
											value={searchInput}
											onChange={(e) => {
												setSearchInput(e.target.value);
												// Clear error when user starts typing
												if (searchError) setSearchError("");
											}}
											onKeyDown={(e) =>
												e.key === "Enter" && handleSearchSubmit(e)
											}
											className="search-input"
											style={{
												width: "100%",
												padding: "10px 40px 10px 40px",
												border: `1px solid ${searchError ? "#ff4d4f" : "#ddd"}`,
												borderRadius: "0px",
												fontSize: "12px",
												boxSizing: "border-box",
												boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
											}}
										/>
										<Search
											style={{
												position: "absolute",
												left: "12px",
												top: "50%",
												transform: "translateY(-50%)",
												color: searchError ? "#ff4d4f" : "#666",
												width: "16px",
												borderRadius: "0px",
												height: "16px",
												pointerEvents: "none",
											}}
										/>
										{searchInput && (
											<button
												type="button"
												onClick={() => {
													setSearchInput("");
													setSearchError("");
													handleSearch("");
												}}
												style={{
													position: "absolute",
													right: "12px",
													top: "50%",
													transform: "translateY(-50%)",
													background: "none",
													border: "none",
													color: searchError ? "#ff4d4f" : "#999",
													cursor: "pointer",
													padding: "4px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "18px",
													lineHeight: 1,
												}}
												aria-label="Clear search"
											>
												×
											</button>
										)}
									</div>
									<button
										type="submit"
										style={{
											padding: "10px 20px",
											backgroundColor: "#1e2832",
											color: "white",
											border: "none",
											borderRadius: "0px",
											fontFamily: "MAN EUROPE_Bold",
											cursor: "pointer",
											fontSize: "14px",
											fontWeight: "bold",
											whiteSpace: "nowrap",
											transition: "background-color 0.2s",
											":hover": {
												backgroundColor: "#b30000",
											},
											height: "35px",
											alignSelf: "flex-start",
										}}
									>
										Search
									</button>
								</div>
								{searchError && (
									<div
										style={{
											color: "#ff4d4f",
											fontSize: "12px",
											marginTop: "-4px",
											paddingLeft: "8px",
										}}
									>
										{searchError}
									</div>
								)}
							</form>
						</div>
					</div>{" "}
					<div
						style={styles.tableContainer}
						className="table-container data-table"
					>
						{" "}
						<PTSachNrDataTable
							ref={dataTableRef}
							onSelectItems={handleSelectItems}
							selectedItems={selectedItems}
							editOptions={editOptions}
							columnVisibility={columnVisibility}
							selectedViewType={selectedViewType} // Pass selectedViewType to DataTable
							onItemsPerPageChange={handleItemsPerPageChange} // Pass the new handler          getUserDisplayName={getUserDisplayName} // Pass user display name function
							getUserEmail={getUserEmail} // Pass user email function for access control
							preventInitialLoad={isFromDashboard} // Prevent initial load when coming from dashboard
							isDashboardLoading={isDashboardFilterLoading} // Pass dashboard loading state
							showMismatchOnly={showMismatchOnly} // Pass mismatch filter flag
						/>
					</div>
				</div>{" "}
			</div>{" "}
			{/* Bulk Edit Modal */}
			<BulkEditModal editOptions={editOptions} />
			{/* Access Denied Modal — shown when bulk save is rejected with HTTP 403 */}
			<Dialog
				open={showBulkAccessDeniedModal}
				onClose={() => setShowBulkAccessDeniedModal(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ style: { borderRadius: "5px", padding: "8px" } }}
			>
				<DialogContent
					style={{ textAlign: "center", padding: "32px 24px 24px 24px" }}
				>
					<div
						style={{
							width: "64px",
							height: "64px",
							backgroundColor: "#1e2832",
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							margin: "0 auto 24px auto",
							border: "3px solid #1e2832",
						}}
					>
						<div style={{ fontSize: "32px", color: "#cc0000" }}>🔒</div>
					</div>
					<Typography
						variant="h6"
						style={{
							marginBottom: "16px",
							fontFamily: "MAN EUROPE_Bold",
							color: "#333",
							fontSize: "20px",
						}}
					>
						Access Denied
					</Typography>
					<Typography
						variant="body1"
						style={{
							color: "#666",
							lineHeight: "1.6",
							fontSize: "16px",
							marginBottom: "8px",
						}}
					>
						You have <strong>read-only</strong> access and cannot make changes.
					</Typography>
					<Typography
						variant="body2"
						style={{ color: "#888", fontSize: "14px" }}
					>
						Please contact your administrator if you need write access.
					</Typography>
				</DialogContent>
				<DialogActions
					style={{ padding: "0 24px 24px 24px", justifyContent: "center" }}
				>
					<Button
						onClick={() => setShowBulkAccessDeniedModal(false)}
						variant="contained"
						style={{
							backgroundColor: "#cc0000",
							color: "white",
							borderRadius: "5px",
							padding: "10px 32px",
							fontFamily: "MAN EUROPE_Bold",
							textTransform: "none",
							fontSize: "14px",
						}}
						onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
						onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
					>
						OK
					</Button>
				</DialogActions>
			</Dialog>
			{/* Email Export Modal */}
			<PTSachNrExportModal
				isOpen={isPTSEmailExportModalOpen}
				onClose={handleClosePTSEmailExportModal}
				userEmail={getUserEmail()}
				currentFilters={{
					view_type: selectedViewType,
					project: selectedProject,
					programm: selectedProgramm,
					purchaseType: selectedPurchaseType,
					purchaseTypeSystem: selectedPurchaseTypeSystem,
					seTeams: selectedSeTeams,
					rampUpGroup: selectedRampUpGroup,
					flag: selectedFlag,
					aenderungsnummer: selectedAenderungsnummer,
					changeDescription: selectedChangeDescription,
					fet: selectedFet,
					dept: selectedDept,
					partNoPrefix: selectedPartNoPrefix,
					search: searchQuery,
					showMismatchOnly: showMismatchOnly,
				}}
			/>
			{/* Supply Type Allocation Tour */}
			<SupplyTypeAllocationJoyrideTour
				isOpen={isTourOpen}
				onClose={handleCloseTour}
			/>
		</div>
	);
};

export default PartsTrackingTool;
